import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { t } from 'i18next';
import classnames from 'classnames';

import { Theme } from '@app/theme';
import { Feature, FieldJump, LogicJumpActionType } from '@app/api';
import { Button, LockFeatureChip } from '@app/core';

import { useFeatureRequiredCallback } from 'components';

import { LogicJumpOption } from './interface';
import { LogicChoiceSelector } from './LogicChoiceSelector';
import { StopIconWithBg } from './StopIconWithBg';
import { findJump } from '../lib/findJump';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.marginBase + 'px',
  },
  selector: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.marginBase + 'px',
  },
  icon: {
    color: theme.colors.grayBorder,
  },
  button: {
    height: theme.height.tiny,
    minWidth: 'auto',
    minHeight: 'auto',
  },
  buttonContent: {
    paddingLeft: theme.marginBase,
    paddingRight: theme.marginBase,
  },
  caption: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
  },
}));

interface Props {
  onChange: (newOption: LogicJumpOption) => any;
  value?: FieldJump;
  small?: boolean;
  logicJumpOptions?: Array<LogicJumpOption>;
  planId?: string;
  isLastField?: boolean;
}

export const LogicDefaultJumpSelector = ({
  onChange,
  value,
  logicJumpOptions,
  planId,
  isLastField,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);

  const onClick = useFeatureRequiredCallback(
    Feature.SCREENING_FORM_LOGIC,
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt(event.currentTarget);
    },
    planId,
  );

  const onClose = useCallback(() => {
    setAnchorElmnt(null);
  }, []);

  const selectedOption = useMemo(() => {
    const res = findJump(logicJumpOptions, value);
    if (res) {
      return res;
    }
    if (!logicJumpOptions?.length) {
      return null;
    }
    return logicJumpOptions[0];
  }, [value, logicJumpOptions]);

  if (!selectedOption || !logicJumpOptions) {
    return null;
  }

  const isEndFormSelected =
    selectedOption.type === LogicJumpActionType.END_FORM ||
    (selectedOption.type === LogicJumpActionType.NEXT_QUESTION && isLastField);

  return (
    <div className={classes.container}>
      <i className={classnames('ri-guide-line', classes.icon)} />
      <LockFeatureChip feature={Feature.SCREENING_FORM_LOGIC} planId={planId} />
      <Button
        variant={isEndFormSelected ? 'transparent' : 'muted'}
        color={isEndFormSelected ? 'inkSecondary' : 'blue'}
        className={classes.button}
        onClick={onClick}
        aria-haspopup="true"
        small
        contentClassName={classes.buttonContent}
      >
        <div className={classes.selector}>
          <>
            {isEndFormSelected && <StopIconWithBg isRoundedSquare />}
            {selectedOption.number
              ? selectedOption.number
              : isEndFormSelected
              ? t(
                  'screeningForm.create.questionBuilder.choiceList.endScreenerJump',
                )
              : selectedOption.labelDefault || selectedOption.label}
            <i className="ri-arrow-down-s-line" />
          </>
        </div>
      </Button>
      <span className={classes.caption}>
        {t('screeningForm.create.questionBuilder.choiceList.defaultLogicJump')}
      </span>
      <LogicChoiceSelector
        anchorElmnt={anchorElmnt}
        logicJumpOptions={logicJumpOptions}
        onChange={onChange}
        selectedLogicJumpOption={selectedOption}
        onClose={onClose}
        defaultLabel
      />
    </div>
  );
};
